import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setPermissions } from "store/slices/appSlice";
import AmplifyAuthService from "services/AmplifyAuthService";

const useUserPermissions = () => {
  // Estado para controlar la carga de datos
  const [loading, setLoading] = useState(true);
  // Estado para almacenar cualquier error ocurrido
  const [error, setError] = useState(null);
  // Estado para almacenar la información del usuario actual (userID y companyID)
  const [currentUser, setCurrentUser] = useState(null);
  // Estado para indicar si ya se inicializó la obtención de permisos (para evitar llamadas duplicadas)
  const [initialized, setInitialized] = useState(false);

  // Referencia para almacenar en caché verificaciones de permisos y evitar cálculos redundantes entre renders
  const permissionsCache = React.useRef(new Set());

  // Clave utilizada para almacenar y recuperar permisos en el localStorage
  const LOCAL_STORAGE_KEY = "userPermissions";

  // Acceso al estado global mediante Redux y función para despachar acciones
  const dispatch = useDispatch();
  // Selecciona los permisos del estado global
  const globalPermissions = useSelector((state) => state.app.userPermissions);

  /**
   * Función asíncrona para obtener los permisos del usuario.
   * Realiza las siguientes tareas:
   * - Evita múltiples llamadas si ya se ha inicializado.
   * - Verifica si la ruta actual es pública y, en ese caso, no realiza la petición.
   * - Obtiene la sesión actual del usuario a través de Amplify.
   * - Extrae el companyID y userID de la sesión.
   * - Verifica si ya existen permisos en el estado global o en el localStorage para evitar peticiones innecesarias.
   */
  const fetchPermissions = useCallback(async () => {
    // Evitar múltiples llamadas si ya se inicializó
    if (initialized) return;

    try {
      setLoading(true);

      // Verificar si es una ruta pública
      const currentPath = window.location.pathname;
      const publicPaths = [
        "/auth/login",
        "/auth/register",
        "/auth/register/es",
        "/auth/register/en",
        "/auth/forgot-password",
      ];
      const publicPathPatterns = [
        /^\/auth\/register\/[a-z]{2}$/, // Para rutas de registro con código de idioma
      ];

      // Si la ruta actual es pública, no se requiere obtener permisos y se termina la función
      if (
        publicPaths.includes(currentPath) ||
        publicPathPatterns.some((pattern) => pattern.test(currentPath))
      ) {
        setLoading(false);
        return;
      }

      // Obtener información de la sesión actual del usuario utilizando Amplify
      const sessionInfo = await AmplifyAuthService.currentSessionRequest();
      const companyID = sessionInfo?.idToken?.payload?.["custom:companyID"];
      const userID = sessionInfo?.idToken?.payload?.["custom:userID"];

      // Si no se encuentran datos esenciales de la sesión, se lanza un error
      if (!companyID || !userID) {
        throw new Error(
          "La sesión actual no contiene un companyID o userID válido."
        );
      }

      // Actualizar el estado con la información del usuario actual
      setCurrentUser({ userID, companyID });

      // Si los permisos ya están almacenados en el estado global para el usuario actual, no se hace nada más
      if (
        globalPermissions.userID === userID &&
        globalPermissions.permissions.length > 0
      ) {
        setLoading(false);
        return;
      }

      // Verificar si hay datos almacenados en localStorage para este usuario
      const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        if (parsedData.userID === userID) {
          // Guardar los permisos en el estado global utilizando dispatch
          dispatch(setPermissions(parsedData));
          setLoading(false);
          return;
        }
      }

      // Si no se encontraron permisos en el estado global ni en el localStorage, se establece un error
      setError("No se encontraron permisos válidos.");
    } catch (err) {
      console.error("Error al obtener permisos:", err);
      setError(err.message || "Error al obtener permisos.");
    } finally {
      // Finalizar la carga y marcar la inicialización como completada
      setLoading(false);
      setInitialized(true);
    }
  }, [dispatch]); // Dependencias: solo depende del dispatch

  /**
   * useEffect que inicializa la obtención de permisos cuando el componente se monta.
   * Se utiliza una variable 'mounted' para evitar actualizar el estado si el componente se desmonta.
   */
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (mounted && !initialized) {
        await fetchPermissions();
      }
    };

    init();

    // Limpieza: evitar actualizar el estado si el componente se desmonta
    return () => {
      mounted = false;
    };
  }, [initialized, fetchPermissions]);

  /**
   * Función para verificar si se tiene acceso a una ruta específica.
   * - Retorna 'true' para rutas públicas.
   * - Para rutas protegidas, compara la ruta con los permisos del usuario almacenados en el estado global.
   *
   * @param {string} path - Ruta que se desea verificar.
   * @param {string} requiredAction - Acción requerida (por defecto "Visualizar").
   * @returns {boolean} - 'true' si se tiene acceso, 'false' en caso contrario.
   */
  const hasAccessToPath = useCallback(
    (path, requiredAction = "Visualizar") => {
      if (!path) return false;
      // Lista de rutas públicas en las que no se requiere verificación de permisos
      const publicPaths = [
        "/auth/login",
        "/auth/register",
        "/auth/register/es",
        "/auth/register/en",
        "/auth/forgot-password",
        "/auth/no-access-permission",
      ];
      if (publicPaths.includes(path)) {
        return true;
      }

      // Verificar si la ruta coincide con un patrón de ruta pública
      const publicPathPatterns = [
        /^\/auth\/register\/[a-z]{2}$/, // Para rutas de registro con código de idioma
      ];

      if (publicPathPatterns.some((pattern) => pattern.test(path))) {
        return true;
      }

      // Si no hay permisos asignados en el estado global, se deniega el acceso
      if (globalPermissions.permissions.length === 0) {
        return false;
      }
      // Se verifica si existe algún permiso que coincida con la ruta y contenga la acción requerida
      const hasAccess = globalPermissions.permissions.some((perm) => {
        const permPath = perm.path;

        return (
          path === permPath &&
          perm.actions.some((actionObj) => actionObj.action === requiredAction)
        );
      });

      return hasAccess;
    },
    [globalPermissions.permissions]
  );

  // Contador (actualmente no utilizado) que podría servir para depuración o seguimiento
  let counter = 0;

  /**
   * Función para verificar si el usuario tiene acceso a una acción específica dentro de un módulo y submódulo.
   * Utiliza una caché para evitar re-evaluar el mismo permiso varias veces.
   *
   * @param {string} module - Módulo al que pertenece el permiso.
   * @param {string} submodule - Submódulo al que pertenece el permiso.
   * @param {string} action - Acción específica a verificar.
   * @returns {boolean} - 'true' si se tiene acceso, 'false' en caso contrario.
   */
  const hasAccessToAction = useCallback(
    (module, submodule, action) => {
      const permissionKey = `${module}|${submodule}|${action}`;

      // Si el permiso ya está en caché, devolver true y evitar cálculos innecesarios
      if (permissionsCache.current.has(permissionKey)) {
        return true;
      }

      // Construcción del objeto de permisos para optimizar la búsqueda
      let objectPermissions = globalPermissions.permissions.reduce(
        (acc, perm) => {
          if (!acc[perm.module]) acc[perm.module] = {};
          if (!acc[perm.module][perm.submodule])
            acc[perm.module][perm.submodule] = [];
          acc[perm.module][perm.submodule] = perm.actions;
          return acc;
        },
        {}
      );

      // Verificar si existen el módulo y submódulo
      const moduleExists = !!objectPermissions[module];
      const submoduleExists =
        moduleExists && !!objectPermissions[module][submodule];

      // Buscar dentro del array de acciones si existe la acción especificada
      const actionExists =
        submoduleExists &&
        objectPermissions[module][submodule].some(
          (perm) => perm.action === action
        );

      // Si el permiso es válido, almacenarlo en la caché para evitar cálculos repetitivos
      if (actionExists) {
        permissionsCache.current.add(permissionKey);
      }

      return actionExists;
    },
    [globalPermissions.permissions]
  );

  /**
   * Función para determinar el tipo de permiso del usuario según los permisos asignados.
   * Se clasifican en:
   * - "superadmin": Tiene acceso a rutas administrativas y de POS.
   * - "admin": Solo tiene acceso a rutas administrativas.
   * - "transaccional": Solo tiene acceso a rutas de POS.
   *
   * @returns {string|null} - Tipo de permiso del usuario o null si no se puede determinar.
   */
  const getUserPermissionType = useCallback(() => {
    if (globalPermissions.permissions.length === 0) {
      return null;
    }

    // Filtrar las rutas que no deben considerarse para determinar el tipo de permiso
    const filteredPermissions = globalPermissions.permissions.filter((perm) => {
      return !["/auth/no-access-permission", "/app/start/shift-start"].includes(
        perm.path
      );
    });

    // Verificar si existe acceso a rutas administrativas (excluyendo rutas de POS)
    const hasAdminAccess = filteredPermissions.some((perm) => {
      return (
        perm.path.startsWith("/app/") && !perm.path.startsWith("/app/pos/")
      );
    });

    // Verificar si existe acceso a rutas de POS
    const hasNonAdminAccess = filteredPermissions.some((perm) => {
      return perm.path.startsWith("/app/pos/");
    });

    if (hasAdminAccess && hasNonAdminAccess) {
      return "superadmin";
    } else if (hasAdminAccess) {
      return "admin";
    } else if (hasNonAdminAccess) {
      return "transaccional";
    }

    return null;
  }, [globalPermissions.permissions]);

  // Extraer información adicional de permisos del usuario desde el estado global
  // Identificadores y nombre relacionados con el rol y permisos asignados
  const userRolePermissionID = globalPermissions.userRolePermissionID || null;
  const roleID = globalPermissions.roleID || null;
  const roleName = globalPermissions.roleName || null;
  // Extraer el userID del estado local currentUser
  const userID = currentUser?.userID || null;

  // Retornar las funciones y variables que permiten acceder y manipular los permisos del usuario
  return {
    permissions: globalPermissions.permissions,
    currentUser,
    hasAccessToPath,
    hasAccessToAction,
    loading,
    error,
    getUserPermissionType,
    userRolePermissionID,
    roleID,
    roleName,
    userID,
  };
};

export default useUserPermissions;
